import { useState, useEffect } from 'react';
import { Users, Plus, Pencil, Trash2, Key, Shield, Activity, ChevronDown, ChevronUp, Eye, EyeOff, X, Check, AlertCircle, Clock, LogIn, LogOut, Save, RotateCcw } from 'lucide-react';
import { useAuth, type CmsUser, type UserRole, type ActivityLog, ROLE_LABELS, ROLE_COLORS, ROLE_PERMISSIONS } from '../context/AuthContext';

const ROLES: { value: UserRole; label: string; desc: string }[] = [
  { value: 'management', label: 'Management', desc: 'Acces complet la toate secțiunile' },
  { value: 'marketing', label: 'Marketing & Content', desc: 'Blog, Pagini, SEO, Produse, Media' },
  { value: 'vanzari', label: 'Vânzări', desc: 'Produse, Comenzi, Prospecți, Clienți, Media' },
];

function genId() { return `usr-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`; }

/* ═══════════════════════════════════════ Modal User Editor ═══════════════ */
function UserModal({ user, onSave, onClose }: {
  user: CmsUser | null; onSave: (u: CmsUser, pw?: string) => void; onClose: () => void;
}) {
  const isNew = !user;
  const [form, setForm] = useState<CmsUser>(user ?? {
    id: genId(), username: '', displayName: '', email: '', role: 'marketing',
    createdAt: new Date().toISOString().slice(0, 10), active: true,
  });
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');

  const set = (k: keyof CmsUser, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.username.trim()) { setError('Username-ul este obligatoriu.'); return; }
    if (!form.displayName.trim()) { setError('Numele afișat este obligatoriu.'); return; }
    if (isNew && !password) { setError('Parola este obligatorie pentru useri noi.'); return; }
    if (password && password !== confirmPw) { setError('Parolele nu coincid.'); return; }
    if (password && password.length < 6) { setError('Parola trebuie să aibă minim 6 caractere.'); return; }
    onSave(form, password || undefined);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#07090f', border: '1px solid rgba(41,92,168,0.3)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-base font-semibold text-white">{isNew ? 'Utilizator Nou' : `Editare: ${user!.displayName}`}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-red-400 bg-red-500/10 border border-red-500/20">
              <AlertCircle size={14} />{error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] text-white/40 mb-1.5 uppercase tracking-wider">Username</label>
              <input value={form.username} onChange={e => set('username', e.target.value.toLowerCase().replace(/\s/g,''))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-all"
                placeholder="username" />
            </div>
            <div>
              <label className="block text-[11px] text-white/40 mb-1.5 uppercase tracking-wider">Nume Afișat</label>
              <input value={form.displayName} onChange={e => set('displayName', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-all"
                placeholder="Ion Popescu" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-white/40 mb-1.5 uppercase tracking-wider">Email</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-all"
              placeholder="email@interpersonal.ro" />
          </div>
          <div>
            <label className="block text-[11px] text-white/40 mb-1.5 uppercase tracking-wider">Rol</label>
            <div className="space-y-2">
              {ROLES.map(r => (
                <label key={r.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${form.role === r.value ? 'border-[#295CA8] bg-[#295CA8]/10' : 'border-white/10 hover:border-white/20'}`}>
                  <input type="radio" name="role" value={r.value} checked={form.role === r.value} onChange={() => set('role', r.value)} className="mt-0.5" />
                  <div>
                    <div className="text-sm text-white font-medium">{r.label}</div>
                    <div className="text-xs text-white/40 mt-0.5">{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] text-white/40 mb-1.5 uppercase tracking-wider">{isNew ? 'Parolă' : 'Parolă Nouă (opțional)'}</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 pr-10 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-all"
                  placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                  {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-[11px] text-white/40 mb-1.5 uppercase tracking-wider">Confirmare Parolă</label>
              <input type={showPw ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-all"
                placeholder="••••••••" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="rounded" />
              <span className="text-sm text-white/70">Cont activ</span>
            </label>
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-white/50 hover:text-white border border-white/10 hover:border-white/20 transition-all">Anulare</button>
          <button onClick={handleSave} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg,#295CA8,#3D72B8)', boxShadow: '0 0 15px rgba(41,92,168,0.3)' }}>
            <Save size={14}/>{isNew ? 'Creează User' : 'Salvează'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════ Modal Change Password ════════════ */
function ChangePasswordModal({ user, onClose }: { user: CmsUser; onClose: () => void }) {
  const { changePassword, currentUser } = useAuth();
  const isManager = currentUser?.role === 'management' && currentUser.id !== user.id;
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const handleSave = () => {
    const res = changePassword(user.id, isManager ? '' : oldPw, newPw);
    if (!res.ok) { setMsg({ ok: false, text: res.error! }); return; }
    if (newPw !== confirmPw) { setMsg({ ok: false, text: 'Parolele nu coincid.' }); return; }
    setMsg({ ok: true, text: 'Parola a fost schimbată cu succes!' });
    setTimeout(onClose, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-sm rounded-2xl overflow-hidden" style={{ background: '#07090f', border: '1px solid rgba(41,92,168,0.3)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-base font-semibold text-white flex items-center gap-2"><Key size={16}/> Schimbare Parolă</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10"><X size={16}/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm text-white/50">User: <span className="text-white font-medium">{user.displayName}</span></div>
          {msg && (
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm border ${msg.ok ? 'text-green-400 bg-green-500/10 border-green-500/20' : 'text-red-400 bg-red-500/10 border-red-500/20'}`}>
              {msg.ok ? <Check size={14}/> : <AlertCircle size={14}/>}{msg.text}
            </div>
          )}
          {!isManager && (
            <div>
              <label className="block text-[11px] text-white/40 mb-1.5 uppercase tracking-wider">Parola Curentă</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={oldPw} onChange={e => setOldPw(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 pr-10 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8]"
                  placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                  {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              </div>
            </div>
          )}
          {isManager && <div className="text-xs text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-xl px-3 py-2">Management poate seta parole direct fără parola veche.</div>}
          <div>
            <label className="block text-[11px] text-white/40 mb-1.5 uppercase tracking-wider">Parolă Nouă</label>
            <input type={showPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8]" placeholder="min. 6 caractere" />
          </div>
          <div>
            <label className="block text-[11px] text-white/40 mb-1.5 uppercase tracking-wider">Confirmare</label>
            <input type={showPw ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8]" placeholder="••••••••" />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-white/50 hover:text-white border border-white/10 transition-all">Anulare</button>
          <button onClick={handleSave} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg,#295CA8,#3D72B8)' }}>
            <Key size={14}/>Schimbă Parola
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════ Sub-tab: Management Useri ════════ */
function UsersManagement() {
  const { allUsers, saveUser, deleteUser, currentUser, loadUsers } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<CmsUser | null>(null);
  const [pwUser, setPwUser] = useState<CmsUser | null>(null);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSave = (u: CmsUser, pw?: string) => { saveUser(u, pw); loadUsers(); };
  const handleDelete = (u: CmsUser) => {
    if (u.id === currentUser?.id) { alert('Nu poți șterge propriul cont!'); return; }
    if (!confirm(`Ștergeți utilizatorul "${u.displayName}"?`)) return;
    deleteUser(u.id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-white">Utilizatori ({allUsers.length})</h2>
        <button onClick={() => { setEditUser(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white transition-all"
          style={{ background: 'linear-gradient(135deg,#295CA8,#3D72B8)', boxShadow: '0 0 12px rgba(41,92,168,0.3)' }}>
          <Plus size={15}/> Utilizator Nou
        </button>
      </div>

      <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden divide-y divide-white/5">
        {allUsers.map(u => (
          <div key={u.id} className="px-5 py-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors">
            {/* Avatar initiale */}
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#295CA8,#3D72B8)' }}>
              {u.displayName.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-white">{u.displayName}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role]}</span>
                {!u.active && <span className="text-[10px] px-2 py-0.5 rounded-full border text-red-400 bg-red-400/10 border-red-400/20">Inactiv</span>}
                {u.id === currentUser?.id && <span className="text-[10px] px-2 py-0.5 rounded-full border text-[#4ab8ff] bg-[#4ab8ff]/10 border-[#4ab8ff]/20">Eu</span>}
              </div>
              <div className="text-xs text-white/30 mt-0.5">@{u.username} · {u.email}</div>
              {u.lastLogin && <div className="text-xs text-white/20 mt-0.5">Ultima accesare: {new Date(u.lastLogin).toLocaleString('ro-RO')}</div>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => setPwUser(u)} title="Schimbă parola"
                className="p-2 rounded-lg text-white/30 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all"><Key size={15}/></button>
              <button onClick={() => { setEditUser(u); setShowModal(true); }} title="Editează"
                className="p-2 rounded-lg text-white/30 hover:text-[#4ab8ff] hover:bg-[#4ab8ff]/10 transition-all"><Pencil size={15}/></button>
              {u.id !== currentUser?.id && (
                <button onClick={() => handleDelete(u)} title="Șterge"
                  className="p-2 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-all"><Trash2 size={15}/></button>
              )}
            </div>
          </div>
        ))}
        {allUsers.length === 0 && (
          <div className="p-8 text-center text-white/30 text-sm">Niciun utilizator.</div>
        )}
      </div>

      {/* Permisiuni per rol */}
      <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2"><Shield size={14}/> Permisiuni per Rol</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {ROLES.map(r => (
            <div key={r.value} className="space-y-2">
              <div className={`text-xs font-semibold px-2 py-1 rounded-lg border inline-block ${ROLE_COLORS[r.value]}`}>{r.label}</div>
              <div className="space-y-1">
                {(['dashboard','blog','media','pages','products','orders','clients','prospects','financial','settings','seo','chatbot','curierat','users'] as const).map(section => {
                  const has = ROLE_PERMISSIONS[r.value]?.includes(section);
                  return (
                    <div key={section} className={`text-xs flex items-center gap-1.5 ${has ? 'text-white/60' : 'text-white/15'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${has ? 'bg-green-400' : 'bg-white/10'}`}/>
                      {section.charAt(0).toUpperCase() + section.slice(1)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && <UserModal user={editUser} onSave={handleSave} onClose={() => { setShowModal(false); setEditUser(null); }} />}
      {pwUser && <ChangePasswordModal user={pwUser} onClose={() => setPwUser(null)} />}
    </div>
  );
}

/* ═══════════════════════════════════════ Sub-tab: Jurnal Activitate ═══════ */
function ActivityLogTab() {
  const { activityLogs, allUsers } = useAuth();
  const [filterUser, setFilterUser] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const filtered = activityLogs.filter(l =>
    (!filterUser || l.userId === filterUser) &&
    (!filterSection || l.section.toLowerCase().includes(filterSection.toLowerCase()))
  );

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const sections = [...new Set(activityLogs.map(l => l.section))];

  const actionIcon = (action: string) => {
    if (action === 'LOGIN') return <LogIn size={12} className="text-green-400"/>;
    if (action === 'LOGOUT') return <LogOut size={12} className="text-red-400"/>;
    if (action === 'CHANGE_PASSWORD') return <Key size={12} className="text-yellow-400"/>;
    return <Activity size={12} className="text-[#4ab8ff]"/>;
  };

  return (
    <div className="space-y-4">
      {/* Filtre */}
      <div className="flex gap-3 flex-wrap">
        <select value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(0); }}
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-all">
          <option value="">Toți utilizatorii</option>
          {allUsers.map(u => <option key={u.id} value={u.id}>{u.displayName}</option>)}
        </select>
        <select value={filterSection} onChange={e => { setFilterSection(e.target.value); setPage(0); }}
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-all">
          <option value="">Toate secțiunile</option>
          {sections.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => { setFilterUser(''); setFilterSection(''); setPage(0); }}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-white/40 hover:text-white border border-white/10 hover:border-white/20 transition-all">
          <RotateCcw size={13}/> Reset
        </button>
        <div className="ml-auto text-xs text-white/30 self-center">{filtered.length} înregistrări</div>
      </div>

      {/* Drill-down per user */}
      {allUsers.length > 0 && (
        <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5">
            <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Drill-down per Utilizator</h3>
          </div>
          {allUsers.map(u => {
            const userLogs = activityLogs.filter(l => l.userId === u.id);
            const isExpanded = expandedUser === u.id;
            return (
              <div key={u.id} className="border-b border-white/5 last:border-0">
                <button onClick={() => setExpandedUser(isExpanded ? null : u.id)}
                  className="w-full flex items-center gap-4 px-5 py-3 hover:bg-white/[0.02] transition-colors text-left">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg,#295CA8,#3D72B8)' }}>
                    {u.displayName.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium">{u.displayName}</div>
                    <div className="text-xs text-white/30">@{u.username} · {userLogs.length} acțiuni înregistrate</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role]}</span>
                  {isExpanded ? <ChevronUp size={14} className="text-white/30"/> : <ChevronDown size={14} className="text-white/30"/>}
                </button>
                {isExpanded && (
                  <div className="px-5 pb-3">
                    <div className="rounded-xl overflow-hidden border border-white/5 divide-y divide-white/5">
                      {userLogs.slice(0, 10).map(l => (
                        <div key={l.id} className="flex items-start gap-3 px-4 py-2.5 bg-black/20">
                          <div className="flex-shrink-0 mt-0.5">{actionIcon(l.action)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-white/70">{l.detail}</div>
                            <div className="text-[10px] text-white/30 mt-0.5">{l.section} · {new Date(l.timestamp).toLocaleString('ro-RO')}</div>
                          </div>
                        </div>
                      ))}
                      {userLogs.length > 10 && <div className="px-4 py-2 text-xs text-white/25 text-center">+{userLogs.length - 10} mai multe în jurnalul complet</div>}
                      {userLogs.length === 0 && <div className="px-4 py-3 text-xs text-white/25 text-center">Nicio activitate înregistrată.</div>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tabel complet jurnal */}
      <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5">
          <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Jurnal Complet</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-4 py-2.5 text-left text-white/30 font-medium">Timestamp</th>
                <th className="px-4 py-2.5 text-left text-white/30 font-medium">Utilizator</th>
                <th className="px-4 py-2.5 text-left text-white/30 font-medium">Acțiune</th>
                <th className="px-4 py-2.5 text-left text-white/30 font-medium">Secțiune</th>
                <th className="px-4 py-2.5 text-left text-white/30 font-medium">Detaliu</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {paged.map(l => (
                <tr key={l.id} className="hover:bg-white/[0.015] transition-colors">
                  <td className="px-4 py-2.5 text-white/40 whitespace-nowrap">
                    <div className="flex items-center gap-1.5"><Clock size={10}/>{new Date(l.timestamp).toLocaleString('ro-RO')}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-white/70 font-medium">{l.displayName}</div>
                    <div className="text-white/30">@{l.username}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">{actionIcon(l.action)}<span className="text-white/60">{l.action}</span></div>
                  </td>
                  <td className="px-4 py-2.5 text-white/50">{l.section}</td>
                  <td className="px-4 py-2.5 text-white/50 max-w-xs truncate" title={l.detail}>{l.detail}</td>
                </tr>
              ))}
              {paged.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-white/25">Nicio activitate înregistrată.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}
              className="px-3 py-1.5 rounded-lg text-xs text-white/40 hover:text-white border border-white/10 hover:border-white/20 disabled:opacity-30 transition-all">← Anterior</button>
            <span className="text-xs text-white/30">Pagina {page+1} din {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages-1, p+1))} disabled={page >= totalPages-1}
              className="px-3 py-1.5 rounded-lg text-xs text-white/40 hover:text-white border border-white/10 hover:border-white/20 disabled:opacity-30 transition-all">Următor →</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════ Main Page ═══════════════════════ */
export default function UsersPage() {
  const [tab, setTab] = useState<'management' | 'activitate'>('management');
  const { currentUser } = useAuth();

  if (currentUser?.role !== 'management') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield size={40} className="text-white/10 mx-auto mb-3"/>
          <div className="text-white/40 text-sm">Acces restricționat · Doar Management</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users size={22}/> Utilizatori</h1>
        <p className="text-white/40 text-sm mt-1">Gestionare utilizatori și jurnal de activitate.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 p-1 rounded-xl border border-white/10" style={{ background: 'rgba(255,255,255,0.02)' }}>
        {([
          { id: 'management', label: 'Management Utilizatori', icon: Users },
          { id: 'activitate', label: 'Jurnal Activitate', icon: Activity },
        ] as const).map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === id ? 'bg-[#295CA8] text-white shadow-lg' : 'text-white/50 hover:text-white hover:bg-white/5'}`}>
            <Icon size={15}/>{label}
          </button>
        ))}
      </div>

      {tab === 'management' && <UsersManagement />}
      {tab === 'activitate' && <ActivityLogTab />}
    </div>
  );
}
